import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { z } from 'zod';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import InputField from '../commonComponent/InputField';
import { FiLayout, FiHome } from 'react-icons/fi';

const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;

const loginSchema = z.object({
  email: z.string().nonempty('Email is required.').email('Invalid email format.'),
  password: z.string().min(1, 'Password is required.'),
});

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();

    const validation = loginSchema.safeParse({ email, password });
    if (!validation.success) {
      toast.error(validation.error.errors[0].message);
      return;
    }
    setLoading(true);

    try {
      const response = await fetch(`${AQ_BASE_URL}/aq/api/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
          localStorage.setItem('roles', JSON.stringify(data.user.roles));
        }

        toast.success('Login successful!');

        if (data.requires_otp_verification) {
          navigate(`/two-factor-otp-verification?user_id=${data.user_id}`);
        }

        if (data.requires_property_registration) {
          navigate('/register-property', { state: { adminUser: data.user } });
        } else if (data.redirect_url) {
          window.location.href = data.redirect_url;
        }
      } else if (data.requires_password_reset) {
        toast.info(data.message || 'Please reset your password.');
        navigate('/reset-password');
      } else {
        if (data.detail === 'User not found') {
          toast.error('No account found with this email address');
        } else if (data.detail === 'Invalid credentials') {
          toast.error('Incorrect password. Please try again.');
        } else if (data.detail === 'No valid roles assigned.') {
          toast.error("You don't have permission to access this system");
        } else if (data.detail && data.detail.includes('Error logging in')) {
          const errorMsg = data.detail.replace('Error logging in: ', '');
          toast.error(errorMsg);
        } else {
          toast.error(data.message || data.detail || 'Login failed');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-gray-100">
      {/* Top Left Logo */}
      <div className="fixed top-0 left-0 p-6 z-10">
        <div className="flex items-center space-x-2">
          <div className="w-10 h-11 bg-black rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xl">EQ</span>
          </div>
          <div>
            <span className="text-2xl font-bold text-gray-900 tracking-tight block">
              EnterpriseQuest
            </span>
            <span className="text-xs text-gray-800 tracking-tight block">
              Powered by RhombusQuest
            </span>
          </div>
        </div>
      </div>

      {/* Login Form Container */}
      <div className="flex min-h-screen items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full p-6 sm:p-8 lg:p-10 bg-white shadow-lg rounded-lg border border-gray-300">
          <h1 className="font-bold text-center text-2xl md:text-3xl text-gray-900">
            Welcome Back..!!
          </h1>
          <h2 className="text-sm text-center font-normal text-gray-500 mb-4">
            Sign in to access your account and continue your journey.
          </h2>
          <h2 className="text-lg text-center font-semibold mt-2 text-black">Login</h2>

          <form onSubmit={handleLogin} className="space-y-4">
            <InputField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
            />
            <InputField
              label="Password"
              value={password}
              type={showPassword ? 'text' : 'password'}
              onChange={(e) => setPassword(e.target.value)}
              showPasswordToggle
              onToggleShowPassword={() => setShowPassword(!showPassword)}
              placeholder="Enter your password"
            />

            <p className="mt-2 text-sm text-gray-600 text-right">
              <Link to="/forgot-password" className="text-gray-950 ml-1 hover:underline">
                Forgot Password?
              </Link>
            </p>

            <button
              type="submit"
              disabled={loading}
              className={`w-full rounded-md py-2 text-sm font-semibold text-white transition duration-300 ${
                loading ? 'bg-gray-600 cursor-not-allowed' : 'bg-black hover:bg-gray-800'
              }`}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
          <p className="mt-6 text-sm text-gray-600 text-center">
            Don't have an account?
            <Link to="/" className="text-gray-950 ml-1 hover:underline">
              Register
            </Link>
          </p>
        </div>
      </div>

      <ToastContainer position="top-center" autoClose={3000} hideProgressBar />
    </div>
  );
};

export default Login;

// import React, { useState } from "react";
// import { useNavigate, Link } from "react-router-dom";
// import { z } from "zod";
// import { ToastContainer, toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import InputField from "../commonComponent/InputField";

// const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;

// const loginSchema = z.object({
//   email: z
//     .string()
//     .nonempty("Email is required.")
//     .email("Invalid email format."),
//   password: z.string().min(1, "Password is required."),
// });

// const Login = () => {
//   const [email, setEmail] = useState("");
//   const [password, setPassword] = useState("");
//   const [showPassword, setShowPassword] = useState(false);
//   const [loading, setLoading] = useState(false); // <-- Added loading state
//   const navigate = useNavigate();
// const handleLogin = async (e) => {
//   e.preventDefault();

//   const validation = loginSchema.safeParse({ email, password });
//   if (!validation.success) {
//     toast.error(validation.error.errors[0].message);
//     return;
//   }
//   setLoading(true);

//   try {
//     const response = await fetch(`${AQ_BASE_URL}/aq/api/login`, {
//       method: "POST",
//       credentials: "include",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({ email, password }),
//     });

//     const data = await response.json();

//     if (response.ok && data.success) {
//       // Store user info
//       if (data.user) {
//         localStorage.setItem("user", JSON.stringify(data.user));
//         localStorage.setItem("roles", JSON.stringify(data.user.roles));
//       }

//       toast.success("Login successful!");

//       if (data.requires_otp_verification) {
//         navigate(`/two-factor-otp-verification?user_id=${data.user_id}`);
//       }

//       if (data.requires_property_registration) {
//         // For admin first login - redirect to property registration
//         navigate("/register-property", { state: { adminUser: data.user } });
//       } else if (data.redirect_url) {
//         // For regular users
//         window.location.href = data.redirect_url;
//       }
//     } else if (data.requires_password_reset) {
//       toast.info(data.message || "Please reset your password.");
//       navigate("/reset-password");
//     } else {
//       // Handle specific error messages from backend
//       if (data.detail === "User not found") {
//         toast.error("No account found with this email address");
//       }
//       else if (data.detail === "Invalid credentials") {
//         toast.error("Incorrect password. Please try again.");
//       }
//       else if (data.detail === "No valid roles assigned.") {
//         toast.error("You don't have permission to access this system");
//       }
//       else if (data.detail && data.detail.includes("Error logging in")) {
//         // Extract the actual error message
//         const errorMsg = data.detail.replace("Error logging in: ", "");
//         toast.error(errorMsg);
//       }
//       else {
//         toast.error(data.message || data.detail || "Login failed");
//       }
//     }
//   } catch (error) {
//     console.error("Login error:", error);
//     toast.error("Network error. Please check your connection and try again.");
//   }
//   finally {
//     setLoading(false);
//   }
// };
//   return (
//     <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4 sm:px-6 lg:px-8">
//       <div className="max-w-md w-full p-6 sm:p-8 lg:p-10 bg-white shadow-lg rounded-lg border border-gray-300">
//         <h1 className="font-bold text-center text-2xl md:text-3xl text-gray-900">
//           Welcome Back..!!
//         </h1>
//         <h2 className="text-sm text-center font-normal text-gray-500 mb-4">
//           Sign in to access your account and continue your journey.
//         </h2>
//         <h2 className="text-lg text-center font-semibold mt-2 text-black">
//           Login
//         </h2>

//         <form onSubmit={handleLogin} className="space-y-4">
//           <InputField
//             label="Email"
//             type="email"
//             value={email}
//             onChange={(e) => setEmail(e.target.value)}
//             placeholder="Enter your email"
//           />
//           <InputField
//             label="Password"
//             value={password}
//             type={showPassword ? "text" : "password"}
//             onChange={(e) => setPassword(e.target.value)}
//             showPasswordToggle
//             onToggleShowPassword={() => setShowPassword(!showPassword)}
//             placeholder="Enter your password"
//           />

//           <p className="mt-2 text-sm text-gray-600 text-right">
//             <Link
//               to="/forgot-password"
//               className="text-gray-950 ml-1 hover:underline"
//             >
//               Forgot Password?
//             </Link>
//           </p>

//           <button
//             type="submit"
//             disabled={loading} // <-- Disable button when loading
//             className={`w-full rounded-md py-2 text-sm font-semibold text-white transition duration-300 ${
//               loading
//                 ? "bg-gray-600 cursor-not-allowed"
//                 : "bg-black hover:bg-gray-800"
//             }`}
//           >
//             {loading ? "Logging in..." : "Login"} {/* <-- Dynamic button text */}
//           </button>
//         </form>
//         <p className="mt-6 text-sm text-gray-600 text-center">
//           Don't have an account?
//           <Link to="/" className="text-gray-950 ml-1 hover:underline">
//             Register
//           </Link>
//         </p>
//       </div>

//       <ToastContainer position="top-center" autoClose={3000} hideProgressBar />
//     </div>
//   );
// };

// export default Login;
